// Main application component combining all MCP server demonstrations

import React, { Suspense, useEffect } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
// import { Analytics } from '@vercel/analytics/react'
// import { SpeedInsights } from '@vercel/speed-insights/react'
import { GalaxyScene } from './scenes/GalaxyScene'
import { ControlPanel } from './components/ControlPanel'
import { PerformanceHUD } from './components/PerformanceHUD'
import { LoadingScreen } from './components/LoadingScreen'
import { useSimulationStore } from './store/simulationStore'

function ErrorFallback({ error, resetErrorBoundary }: any) {
  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center">
      <div className="bg-gray-800 p-8 rounded-lg max-w-lg text-center space-y-4">
        <h2 className="text-2xl font-bold text-red-400">WebGL Error</h2>
        <p className="text-gray-300">
          Unable to initialize the 3D simulation. This may be due to:
        </p>
        <ul className="text-left text-gray-400 space-y-2">
          <li>• WebGL not supported in your browser</li>
          <li>• Hardware acceleration disabled</li>
          <li>• GPU drivers need updating</li>
        </ul>
        <button
          onClick={resetErrorBoundary}
          className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
        >
          Try Again
        </button>
      </div>
    </div>
  )
}

function App() {
  const qualityLevel = useSimulationStore((state) => state.qualityLevel)

  useEffect(() => {
    // Log performance metrics
    const logPerformance = () => {
      if ('memory' in performance) {
        const memory = (performance as any).memory
        console.log('Memory usage:', {
          used: Math.round(memory.usedJSHeapSize / 1048576) + 'MB',
          total: Math.round(memory.totalJSHeapSize / 1048576) + 'MB'
        })
      }
    }

    const interval = setInterval(logPerformance, 10000)
    return () => clearInterval(interval)
  }, [])

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="relative w-full h-screen bg-gray-950 overflow-hidden">
        {/* 3D Scene */}
        <Suspense fallback={<LoadingScreen />}>
          <div className="absolute inset-0">
            <GalaxyScene />
          </div>
        </Suspense>

        {/* UI Overlay */}
        <div className="relative z-10 pointer-events-none">
          <div className="pointer-events-auto">
            <ControlPanel />
            <PerformanceHUD />
          </div>
        </div>

        {/* Title */}
        <div className="absolute top-4 left-4 z-20">
          <h1 className="text-4xl font-bold text-white">
            Galaxy Simulation
          </h1>
          <p className="text-gray-400 mt-2">
            Production-ready 3D visualization powered by MCP servers
          </p>
          <div className="flex gap-2 mt-4">
            <span className="px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm">
              Sequential-Thinking
            </span>
            <span className="px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm">
              Context7
            </span>
            <span className="px-3 py-1 bg-purple-500/20 text-purple-400 rounded-full text-sm">
              Magic UI
            </span>
            <span className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-sm">
              Puppeteer Tests
            </span>
          </div>
        </div>

        {/* Quality indicator */}
        <div className="absolute bottom-4 left-4 z-20">
          <div className="bg-gray-800/80 backdrop-blur rounded-lg px-4 py-2">
            <span className="text-xs text-gray-400">Quality: </span>
            <span className="text-sm font-medium text-white capitalize">
              {qualityLevel}
            </span>
          </div>
        </div>

        {/* Analytics */}
        {/* <Analytics /> */}
        {/* <SpeedInsights /> */}
      </div>
    </ErrorBoundary>
  )
}

export default App